/**
 * Shared JSON-LD builder used by both the runtime <SEO/> component (for
 * client-side updates) and the build-time prerender script (for the static
 * HTML we ship to bots). Keeping the logic in one place guarantees they
 * never drift.
 */

import { SITE_URL, SITE_NAME } from "./seo-config";

// Routes that represent product features — emit a Schema.org Service node
// in addition to WebPage for stronger AI/search comprehension.
export const FEATURE_ROUTES: ReadonlySet<string> = new Set<string>([
  "/construction-photo-documentation",
  "/construction-progress-tracking",
  "/construction-reports",
  "/construction-daily-log",
  "/construction-document-management",
  "/ai-for-construction",
  "/ai-construction-estimating",
  "/construction-risk-management",
  "/contractor-invoicing",
  "/construction-scheduling",
  "/share-updates",
  "/punch-list-software",
  "/construction-compliance",
  "/annotations",
  "/tags-labels",
  "/team-workflows",
  "/project-dashboard",
  "/portfolio",
  "/procurement-ai",
  "/marketing-ai",
]);

export type JsonLdInput = {
  /** Canonical (English-style) route, e.g. "/pricing" or "/blog/foo". */
  canonicalRoute: string;
  lang: string;
  /** Final, brand-suffixed title. */
  title: string;
  description: string;
  image: string;
  /** Optional override; defaults to derived buildUrl. */
  canonical?: string;
  /** "blog-post" emits an extra BlogPosting node. */
  kind?: "page" | "blog-post";
  /** Original publication date, ISO 8601 (e.g. "2025-09-18T00:00:00+00:00"). Immutable. */
  datePublished?: string;
  /** Last meaningful content edit, ISO 8601. Falls back to datePublished. */
  dateModified?: string;
  /** Optional Q&A pairs. When provided, emits a FAQPage node in @graph. */
  faqItems?: Array<{ q: string; a: string }>;
};

// BCP 47 locale tags for schema.org inLanguage (more precise than bare ISO 639-1).
const SCHEMA_LOCALE: Record<string, string> = {
  en: "en-US",
  ar: "ar-SA",
  hi: "hi-IN",
  ru: "ru-RU",
};

// Build a fully-localized URL for a given route + lang.
// Mirrors withLangPrefix from src/lib/i18n-routing.ts but is duplicated
// here so this module stays usable in node-only contexts (prerender script).
function localizedUrl(canonicalRoute: string, lang: string): string {
  const clean = canonicalRoute.startsWith("/") ? canonicalRoute : `/${canonicalRoute}`;
  if (lang === "en") {
    return `${SITE_URL}${clean === "/" ? "" : clean}`;
  }
  if (clean === "/") return `${SITE_URL}/${lang}`;
  return `${SITE_URL}/${lang}${clean}`;
}

// Core feature pages shown in the site navigation mega-menu.
// Order matches the header dropdown — this order influences which pages
// Google selects as sitelinks.
const SITE_NAVIGATION_ELEMENTS = [
  { name: "AI Construction Progress Tracking", url: `${SITE_URL}/construction-progress-tracking` },
  { name: "AI Construction Photo Documentation", url: `${SITE_URL}/construction-photo-documentation` },
  { name: "AI Construction Reports", url: `${SITE_URL}/construction-reports` },
  { name: "AI Construction Estimating", url: `${SITE_URL}/ai-construction-estimating` },
  { name: "AI Construction Risk Management", url: `${SITE_URL}/construction-risk-management` },
  { name: "AI Assistant for Construction", url: `${SITE_URL}/ai-for-construction` },
  { name: "AI Construction Punch List", url: `${SITE_URL}/punch-list-software` },
  { name: "Construction Daily Log", url: `${SITE_URL}/construction-daily-log` },
  { name: "Construction Document Management", url: `${SITE_URL}/construction-document-management` },
  { name: "AI Construction Scheduling", url: `${SITE_URL}/construction-scheduling` },
  { name: "Construction Compliance Software", url: `${SITE_URL}/construction-compliance` },
  { name: "AI for Villa Construction", url: `${SITE_URL}/ai-for-villa-construction` },
  { name: "AI for General Contractors", url: `${SITE_URL}/ai-for-general-contractors` },
  { name: "Pricing & Plans", url: `${SITE_URL}/pricing` },
  { name: "About Banamind", url: `${SITE_URL}/about` },
];

export function buildPageJsonLd(input: JsonLdInput) {
  const {
    canonicalRoute,
    lang,
    title,
    description,
    image,
    kind = "page",
    datePublished,
    dateModified,
  } = input;

  // URL of the actual page being rendered (locale-aware). This MUST match
  // the <link rel="canonical"> tag on the page so that schema and canonical
  // don't contradict each other. Each language version is its own WebPage
  // entity with its own URL.
  const localizedCanonical = input.canonical ?? localizedUrl(canonicalRoute, lang);

  // English/source URL. Used for translationOfWork backreferences so AI
  // and search engines can connect the localized version to the canonical
  // English source. Also used for sitewide Service entity ID so we don't
  // create 4 duplicate Service entries (one per language).
  const englishCanonical = localizedUrl(canonicalRoute, "en");

  const inLanguageTag = SCHEMA_LOCALE[lang] ?? lang;
  const isLocalized = lang !== "en";

  const breadcrumbItems = [
    { name: "Home", url: localizedUrl("/", lang) },
    ...(canonicalRoute !== "/"
      ? [{ name: title.replace(` | ${SITE_NAME}`, ""), url: localizedCanonical }]
      : []),
  ];

  const webPage: Record<string, unknown> = {
    "@type": "WebPage",
    "@id": `${localizedCanonical}#webpage`,
    url: localizedCanonical,
    name: title,
    description,
    inLanguage: inLanguageTag,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    primaryImageOfPage: { "@type": "ImageObject", url: image },
  };
  // For non-English pages, point back to the English source as the canonical work.
  if (isLocalized) {
    webPage.translationOfWork = {
      "@type": "WebPage",
      "@id": `${englishCanonical}#webpage`,
      url: englishCanonical,
      inLanguage: "en-US",
    };
  }

  const graph: Record<string, unknown>[] = [
    webPage,
    {
      "@type": "BreadcrumbList",
      "@id": `${localizedCanonical}#breadcrumb`,
      itemListElement: breadcrumbItems.map((item, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        name: item.name,
        item: item.url,
      })),
    },
  ];

  // On the homepage, emit WebSite + Organization + SiteNavigationElement so
  // Google knows the authoritative site identity and which pages to surface
  // as sitelinks. Only emit for the English root — canonical sitewide entities
  // don't need to be repeated on every locale variant.
  if (canonicalRoute === "/" && lang === "en") {
    graph.push({
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: SITE_NAME,
      description,
      inLanguage: "en-US",
      publisher: { "@id": `${SITE_URL}/#organization` },
    });
    graph.push({
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: `${SITE_URL}/logo.png`,
        width: 512,
        height: 512,
      },
      sameAs: [
        "https://www.linkedin.com/company/banamind",
        "https://twitter.com/banamind",
      ],
      description,
    });
    // SiteNavigationElement — explicit navigation structure.
    // This is the strongest schema signal for influencing Google Sitelinks.
    graph.push({
      "@type": "ItemList",
      "@id": `${SITE_URL}/#sitenavigation`,
      name: "Site Navigation",
      itemListElement: SITE_NAVIGATION_ELEMENTS.map((item, idx) => ({
        "@type": "SiteNavigationElement",
        position: idx + 1,
        name: item.name,
        url: item.url,
      })),
    });
  }

  if (FEATURE_ROUTES.has(canonicalRoute)) {
    // Service is a sitewide entity — one Service per feature, available in
    // multiple languages. Anchor ID to the English URL so all 4 language
    // pages reference the same Service in the Knowledge Graph. The localized
    // page's WebPage is what's per-language; the underlying Service is not.
    graph.push({
      "@type": "Service",
      "@id": `${englishCanonical}#service`,
      name: title.replace(` | ${SITE_NAME}`, ""),
      description,
      serviceType: "Construction project management software feature",
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: ["Saudi Arabia", "United Arab Emirates", "India", "Worldwide"],
      audience: {
        "@type": "BusinessAudience",
        audienceType: "Construction contractors and project managers",
      },
      url: localizedCanonical,
      inLanguage: inLanguageTag,
    });
  }

  if (kind === "blog-post") {
    const blogPosting: Record<string, unknown> = {
      "@type": "BlogPosting",
      "@id": `${localizedCanonical}#blogposting`,
      headline: title.replace(` | ${SITE_NAME}`, ""),
      description,
      image: [image],
      url: localizedCanonical,
      inLanguage: inLanguageTag,
      // Emit honest freshness signals: datePublished is the original publication
      // date (immutable), dateModified reflects the last real content edit.
      // Falls back to datePublished when no separate modification timestamp is
      // available — better than omitting dateModified entirely.
      ...(datePublished
        ? { datePublished, dateModified: dateModified ?? datePublished }
        : {}),
      author: { "@type": "Organization", name: SITE_NAME, "@id": `${SITE_URL}/#organization` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: { "@type": "WebPage", "@id": `${localizedCanonical}#webpage` },
    };
    // For translated posts, link back to the English source post.
    if (isLocalized) {
      blogPosting.translationOfWork = {
        "@type": "BlogPosting",
        "@id": `${englishCanonical}#blogposting`,
        url: englishCanonical,
        inLanguage: "en-US",
      };
    }
    graph.push(blogPosting);
  }

  // FAQPage — emitted into @graph so AI crawlers without JS rendering still
  // see the FAQ schema. Avoids the React-Helmet runtime injection that
  // GPTBot / ClaudeBot / PerplexityBot can't execute.
  if (input.faqItems && input.faqItems.length > 0) {
    graph.push({
      "@type": "FAQPage",
      "@id": `${localizedCanonical}#faq`,
      inLanguage: inLanguageTag,
      mainEntity: input.faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    });
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
